import { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap, Circle } from "react-leaflet";
import L from "leaflet";
import { useNavigate } from "react-router-dom";
import "leaflet/dist/leaflet.css";
import { getVenueImage } from "../utils/defaultImages";

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

// Brand color from logo
const BRAND_COLOR = "#2DB2AE";
const FEATURED_COLOR = "#F59E0B";

// Custom marker icon - supports custom logo for premium venues
const createCustomIcon = (isPremium, isSelected, customLogoUrl = null) => {
  const color = isPremium ? FEATURED_COLOR : BRAND_COLOR;
  const size = isSelected ? 44 : 36;
  
  // If premium venue has a custom logo, show it in the marker
  if (isPremium && customLogoUrl) {
    return L.divIcon({
      className: "custom-marker-logo",
      html: `
        <div class="venue-marker-pin" style="
          position: relative;
          width: ${size}px;
          height: ${size}px;
          background: white;
          border-radius: 50%;
          border: 3px solid ${color};
          box-shadow: 0 2px 10px rgba(0,0,0,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          z-index: 1000;
        ">
          <img src="${customLogoUrl}" style="
            width: ${size - 8}px;
            height: ${size - 8}px;
            object-fit: contain;
            border-radius: 50%;
          " onerror="this.style.display='none'" />
        </div>
        <div style="
          width: 0;
          height: 0;
          border-left: 8px solid transparent;
          border-right: 8px solid transparent;
          border-top: 10px solid ${color};
          margin: -2px auto 0;
          position: relative;
          z-index: 1000;
        "></div>
      `,
      iconSize: [size, size + 10],
      iconAnchor: [size / 2, size + 10],
      popupAnchor: [0, -size - 5],
    });
  }
  
  // Standard marker
  return L.divIcon({
    className: "custom-marker",
    html: `
      <div class="venue-marker-pin" style="
        position: relative;
        width: ${size}px;
        height: ${size}px;
        background: ${color};
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        border: 2px solid white;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
      ">
        <div style="
          transform: rotate(45deg);
          color: white;
          font-size: ${isSelected ? '12px' : '10px'};
          font-weight: 600;
        ">
          ${isPremium ? '★' : '●'}
        </div>
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
    popupAnchor: [0, -size],
  });
};

// User location icon
const userLocationIcon = L.divIcon({
  className: "user-location-marker",
  html: `
    <div style="
      width: 16px;
      height: 16px;
      background: ${BRAND_COLOR};
      border-radius: 50%;
      border: 3px solid white;
      box-shadow: 0 2px 8px rgba(45, 178, 174, 0.4);
    "></div>
  `,
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

function MapController({ center, zoom }) {
  const map = useMap();
  
  useEffect(() => {
    if (center && map && map._container && document.contains(map._container)) {
      try {
        map.setView(center, zoom || map.getZoom(), { animate: true });
      } catch (e) {
        // Silently ignore - map may have been unmounted
      }
    }
  }, [center, zoom, map]);
  
  return null;
}

// Custom marker that pans map on click and returns on close
function VenueMarker({ venue, icon, zIndexOffset, interactive, navigate }) {
  const map = useMap();
  const markerRef = useRef(null);

  const handleMarkerClick = () => {
    if (!map || !map._container || !venue?.location?.lat || !venue?.location?.lng) return;
    
    try {
      // Pan map to show popup (offset slightly so popup is visible)
      const markerPos = [venue.location.lat, venue.location.lng];
      // Offset the pan slightly upward so popup doesn't cover the marker
      const offsetLat = markerPos[0] + 0.002;
      
      // Check if map is still valid before panning
      if (map._container && document.contains(map._container)) {
        map.panTo([offsetLat, markerPos[1]], { animate: true, duration: 0.3 });
      }
    } catch (e) {
      // Silently ignore - map may have been unmounted
    }
  };

  const handlePopupClose = () => {
    if (!map || !map._container || !venue?.location?.lat || !venue?.location?.lng) return;
    
    try {
      // Return map to center on the marker
      const markerPos = [venue.location.lat, venue.location.lng];
      
      // Check if map is still valid before panning
      if (map._container && document.contains(map._container)) {
        map.panTo(markerPos, { animate: true, duration: 0.3 });
      }
    } catch (e) {
      // Silently ignore - map may have been unmounted
    }
  };

  // Don't render marker if location is invalid
  if (!venue?.location?.lat || !venue?.location?.lng) {
    return null;
  }

  return (
    <Marker
      ref={markerRef}
      position={[venue.location.lat, venue.location.lng]}
      icon={icon}
      zIndexOffset={zIndexOffset}
      eventHandlers={{
        click: handleMarkerClick,
        popupclose: handlePopupClose,
      }}
    >
      <Popup>
        <div className="p-2 min-w-[200px]">
          <div className="flex items-start gap-2">
            <img
              src={getVenueImage(venue, 200)}
              alt={venue.name}
              className="w-14 h-14 rounded-lg object-cover"
            />
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-gray-900 text-sm truncate">{venue.name}</h4>
              <p className="text-xs text-gray-500 capitalize">{venue.venue_type.replace("_", " ")}</p>
              <div className="flex items-center gap-1 mt-1">
                <span className="text-amber-500">★</span>
                <span className="text-sm font-medium">{venue.rating.toFixed(1)}</span>
              </div>
            </div>
          </div>
          {interactive && (
            <button
              onClick={() => navigate(`/venue/${venue.id}`)}
              className="mt-2 w-full py-1.5 bg-brand text-white text-xs font-medium rounded-lg hover:bg-brand-dark transition-colors"
              style={{ backgroundColor: BRAND_COLOR }}
            >
              View Details
            </button>
          )}
        </div>
      </Popup>
    </Marker>
  );
}

export default function VenueMap({ 
  venues, 
  center = [51.5074, -0.1278], 
  zoom = 12,
  selectedVenue = null,
  userLocation = null,
  interactive = true 
}) {
  const navigate = useNavigate();
  
  // Detect mobile for hiding zoom controls
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  // Sort venues for proper z-index layering:
  // 1. Non-premium venues first (lowest rating first, so highest rating renders on top)
  // 2. Premium venues last (always on top of non-premium)
  const sortedVenues = [...venues].sort((a, b) => {
    // Premium always goes after non-premium (renders on top)
    if (a.is_premium && !b.is_premium) return 1;
    if (!a.is_premium && b.is_premium) return -1;
    
    // For same premium status, sort by rating (lowest first, so highest renders on top)
    const ratingA = a.rating || 0;
    const ratingB = b.rating || 0;
    return ratingA - ratingB; // Lower rating renders first (below), higher rating renders last (on top)
  });

  // Calculate zIndexOffset based on premium status and rating
  const getZIndexOffset = (venue) => {
    if (venue.is_premium) {
      // Premium venues: base 1000 + rating bonus (0-100)
      return 1000 + Math.round((venue.rating || 0) * 10);
    }
    // Non-premium venues: rating-based offset (0-100)
    return Math.round((venue.rating || 0) * 10);
  };

  return (
    <MapContainer
      center={center}
      zoom={zoom}
      scrollWheelZoom={interactive}
      dragging={interactive}
      zoomControl={interactive && !isMobile}
      style={{ height: "100%", width: "100%" }}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <MapController center={center} zoom={zoom} />
      
      {/* User Location */}
      {userLocation && (
        <>
          <Marker position={[userLocation.lat, userLocation.lng]} icon={userLocationIcon}>
            <Popup>
              <div className="text-center p-1">
                <p className="font-medium text-sm">You are here</p>
              </div>
            </Popup>
          </Marker>
          <Circle
            center={[userLocation.lat, userLocation.lng]}
            radius={300}
            pathOptions={{
              color: BRAND_COLOR,
              fillColor: BRAND_COLOR,
              fillOpacity: 0.1,
              weight: 1
            }}
          />
        </>
      )}
      
      {/* Venue Markers - Premium venues render last (on top), then by rating */}
      {sortedVenues.map((venue) => (
        <VenueMarker
          key={venue.id}
          venue={venue}
          icon={createCustomIcon(
            venue.is_premium, 
            selectedVenue === venue.id,
            venue.is_premium ? venue.custom_logo_url : null
          )}
          zIndexOffset={getZIndexOffset(venue)}
          interactive={interactive}
          navigate={navigate}
        />
      ))}
    </MapContainer>
  );
}
